<template>
  <div
    id="tab-feedback"
    class="tab-feedback"
  >
    <div class="tab-feedback-content">
      <div class="feedback-rating">
        <div
          class="tab-feedback-top"
          :class="{
            light: isLightTheme,
            dark: !isLightTheme,
          }"
        >
          <div class="form-title">
            {{ $t("How's the presentation?") }}
          </div>
          <div class="rating">
            <div class="rating-content">
              <StarRating
                class="rating-feedback"
                size="36px"
                :rate="rate"
                @onChange="handleChangeRate"
              />
            </div>
          </div> <!-- /.rating -->
        </div>
      </div>

      <div class="tab-container">
        <div
          class="text-box"
          :class="{
            error: feedbackMsg.length > maxFeedbackMsgLength
          }"
        >
          <AutoSaveTextArea
            v-model="feedbackMsg"
            :uniqueId="feedbackFormId"
            rows="3"
            :placeholder="$t('Leave feedback...')"
            :maxlength="maxFeedbackMsgLength"
            @focus="onFocusCommentInput"
          />
          <div class="count-char">
            {{ maxFeedbackMsgLength - feedbackMsg.length }}
          </div>
        </div>
        <div class="horizontal layout center">
          <div class="avatar">
            <div v-if="!hasAudienceName" class="default-ava">
              <i class="fas fa-user" />
            </div>
            <div
              v-if="hasAudienceName"
              class="color-ava"
              :style="{
                'background-color': audienceColor
              }"
            >
              {{ questionerName | textAvatar }}
            </div>
          </div>
          <input
            :value="questionerName"
            type="text"
            :placeholder="_getPlaceHolder()"
            @change="setQuestionerName"
            class="flex input-name"
            :class="{
              error: isQuestionerExceedMaxLength
            }"
            :maxlength="maxAudienceNameLength"
          >
          <button
            class="button-submit horizontal layout center center-justified"
            :disabled="isDisableSubmit()"
            @click="sendFeedback()"
          >
            {{ $t('Send') }}
            <span v-if="submitting" class="loading-spinner" />
          </button>
        </div>
        <div v-if="textError" class="text-error text-danger">
          {{ textError }}
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex'
import * as Sentry from '@sentry/vue'
import AutoSaveTextArea from '@/components/common/AutoSaveTextArea.vue'
import Utils from '../util'
import storage from '../services/storage'

const MAX_FEEDBACK_MSG_LENGTH = 200
const MAX_AUDIENCE_NAME_LENGTH = 20
const FEEDBACK_FORM_ID = 'FEEDBACK_FORM'

export default {
  components: {
    AutoSaveTextArea,
  },
  data() {
    return {
      submitting: false,
      feedbackMsg: '',
      maxFeedbackMsgLength: MAX_FEEDBACK_MSG_LENGTH,
      maxAudienceNameLength: MAX_AUDIENCE_NAME_LENGTH,
      feedbackFormId: FEEDBACK_FORM_ID,
      audienceColor: '',
      textError: '',
      rate: null,
      questionerName: null,
    }
  },

  computed: {
    ...mapState({
      audience: (state) => state.audience,
      audienceId: (state) => state.audienceId,
      presentation: (state) => state.presentation,
      textColour: (state) => state.slide.textColour,
    }),

    isLightTheme() {
      return this.$store.state.slide && this.$store.state.slide.textColour === '#313131'
    },

    hasAudienceName() {
      const name = this.questionerName

      if (typeof name !== 'string' || !name.trim()) return false

      return true
    },

    isQuestionerExceedMaxLength() {
      return this.questionerName &&
        this.questionerName.length > this.maxAudienceNameLength
    },

  },

  mounted() {
    this.initAudienceColor()
    this.initAudienceName()
    this.initRate()
  },

  methods: {
    ...mapActions([
      'sendFeedbackAction',
    ]),
    initAudienceColor() {
      if (!this.audienceId) {
        this.audienceColor = '#313131'
      } else {
        this.audienceColor = Utils.getColorByStr(this.audienceId)
      }
    },

    initAudienceName() {
      const presentationId = this.presentation && this.presentation.id
      this.questionerName = storage.getValueByPresentation(
        presentationId,
        'questionerName',
        '',
      )

      if (this.$store.state.audienceName && !this.questionerName) {
        this.questionerName = this.$store.state.audienceName
        this.saveAudienceName()
      }
    },

    initRate() {
      const presentationId = this.presentation && this.presentation.id

      this.rate = storage.getValueByPresentation(
        presentationId,
        'feedbackRating',
        null,
      )
    },

    setQuestionerName(e) {
      this.questionerName = e.target.value
      this.saveAudienceName()
    },

    saveAudienceName() {
      this.textError = ''

      storage.setValueByPresentation(
        this.presentation.id,
        'questionerName',
        this.questionerName,
      )
    },

    isDisableSubmit() {
      const isExceedLimit = this.feedbackMsg && this.feedbackMsg.length > this.maxFeedbackMsgLength
      const title = this.feedbackMsg && this.feedbackMsg.trim()

      return isExceedLimit || this.submitting || !title
    },

    async handleChangeRate(value) {
      if (this.rate === value) return

      this.rate = value

      const payload = {
        voteCount: parseInt(this.rate, 10),
        createdBy: this.audience.id,
        presentationId: this.presentation.id,
        audienceName: this.questionerName,
      }

      try {
        await this.sendFeedbackAction(payload)

        Utils.showToastSuccess(this.$t('Rating sent!'))

        storage.setValueByPresentation(
          this.presentation.id,
          'feedbackRating',
          this.rate,
        )

        Utils.ga4Track('Audience feedback > Rate presentation', {
          eventLabel: this.rate,
        })
      } catch (error) {
        Sentry.captureException(error)
        this.textError = this.$t('Something went wrong, please try again.')
      } finally {
        this.submitting = false
      }
    },

    onFocusCommentInput() {
      Utils.ga4Track('Audience feedback > Focus input')
    },

    async sendFeedback() {
      const title = this.feedbackMsg && this.feedbackMsg.trim()

      if (this.submitting || !title) return

      this.textError = ''

      if (this.isQuestionerExceedMaxLength) {
        this.textError = this.$t('Your name is limited to {length} characters.', {
          length: this.maxAudienceNameLength,
        })

        return
      }

      this.submitting = true
      const payload = {
        audienceId: this.audience.audienceId,
        createdBy: this.audience.id,
        audienceColor: this.audienceColor,
        presentationId: this.presentation.id,
        voteCount: parseInt(this.rate, 10),
        title,
        audienceName: this.questionerName,
      }

      try {
        await this.sendFeedbackAction(payload)
        Utils.showToastSuccess(this.$t('Feedback sent!'))
        this.feedbackMsg = ''

        storage.setValueByPresentation(
          this.presentation.id,
          'feedbackRating',
          this.rate,
        )
        Utils.ga4Track('Audience feedback > Submit', {
          comment: title,
          rating: this.rate,
        })
      } catch (error) {
        Sentry.captureException(error)
        this.textError = this.$t('Something went wrong, please try again.')
      } finally {
        this.submitting = false
      }
    },

    _getPlaceHolder() {
      return this.$t('enterYourNameOptional')
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";

.tab-feedback {
  input[type='text'] {
    height: 40px;
    border: none;
    background-color: #fff;
    padding-right: 8px;
    min-width: 50px;
    border-radius: 4px;
    border: 1px solid #ddd;
  }

  .button-submit {
    width: 90px;
    font-size: 14px;
    text-transform: capitalize;
    padding: 0;
    text-align: center;
    height: 40px;
    line-height: 40px;
    font-weight: bold;
    border-radius: 20px;
    background-color: $brand-a100;

    i {
      font-size: 14px;
    }

    .loading-spinner {
      margin-left: 4.5px;
      width: 1.2em;
      height: 1.2em;
    }
  }
}

.feedback-rating {
  margin: 0 auto;
  padding-top: 16px;
  padding-bottom: 16px;
  padding-left: 16px;
  padding-right: 16px;
  max-width: 600px;
}

.tab-feedback-top {
  padding: 20px 20px 16px 20px;
  border-radius: 4px;

  &.light {
    color: #313131;
    background-color: #F0F4FA;

    .rating-desc {
      color: #707070;
    }
  }

  &.dark {
    color: #fff;
    background-color: #6B6B6B;

    .rating-desc {
      color: #D1D1D1;
    }
  }
}

.form-title {
  font-weight: 700;
  text-align: center;
}

.input-name {
  padding-left: 8px;
  margin-right: 8px;
  line-height: 40px;
  -webkit-appearance: none;

  &.error {
    color: $danger-color !important;
  }
}

.text-error {
  text-align: center;
  font-size: 14px;
}

.tab-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 0 16px 16px 16px;
}

.text-box {
  position: relative;

  textarea {
    width: 100%;
    padding: 16px;
    border-radius: 4px;
    border: 1px solid #ddd;
    -webkit-appearance: none;
    outline: none;
    padding-bottom: 20px;
  }

  .count-char {
    position: absolute;
    bottom: 8px;
    right: 8px;
    font-size: 14px;
    color: #666;
  }

  &.error {
    textarea {
      border-color: $danger-color;
      color: $danger-color;
    }

    .count-char {
      color: $danger-color;
    }
  }
}

.avatar {
  .default-ava,
  .color-ava {
    background-color: $primary-color;
    width: 32px;
    height: 32px;
    text-align: center;
    line-height: 32px;
    border-radius: 50%;
    margin: 16px;
    border: 1px solid #fff;
    margin-right: 8px;
    margin-left: 0;

    i {
      color: #fff;
    }
  }

  .color-ava {
    color: #fff;
    text-transform: uppercase;
    font-size: 13px;
    font-weight: bold;
  }
}

.rating {
  position: relative;

  &-content {
    position: relative;
    max-width: 250px;
    margin-left: auto;
    margin-right: auto;
    padding-top: 12px;
  }

  &-feedback {
    display: flex;
    justify-content: center;
  }

  &-desc {
    visibility: hidden;
    text-align: center;
    font-size: 14px;
    line-height: 18px;

    &.visible {
      visibility: visible;
    }
  }
}
</style>
